import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Button } from '@/components/Button';

/**
 * 使い勝手フィードバック⑥.5 (D-U32/D-U35/D-U42/D-U45): 承認通知の送信可否・経路設定マトリクス。
 * 設計: docs/design/v04-request-workflow-mece-detail.md §8-5
 *
 * `/request-types/:id/notifications` (①層) と `/approvals/workflow-patterns/:id/notifications`
 * (②層) の両方から共有される (形状が完全に同一のため、§8-93 型の二重実装を避ける)。
 */

type EventTypeInfo = {
  event_type_id: string;
  label_key: string;
  default_channels: string[];
  opt_out_allowed: boolean;
};

type RecipientRole = 'approver' | 'watcher';

type DeliveryRow = {
  event_type_id: string;
  recipient_role: RecipientRole;
  is_enabled: boolean;
  channels: string[] | null;
  source: 'request_type' | 'workflow' | 'default';
  overridden_by_request_type_count?: number;
};

type MatrixResponse = { event_types: EventTypeInfo[]; rows: DeliveryRow[] };

const EVENT_TYPE_LABELS: Record<string, string> = {
  'approval.pending': '承認待ちの通知',
  'approval.watch_notice': '決裁結果の共有通知',
  'approval.returned': '差し戻しの通知',
  'approval.auto_approved': '受理通知 (ゼロ段階承認)',
};

const ROLE_LABELS: Record<RecipientRole, string> = { approver: '承認者', watcher: '共有先' };

const CHANNEL_LABELS: Record<string, string> = { in_app: 'アプリ内', email: 'メール', line: 'LINE' };
const ALL_CHANNELS = ['in_app', 'email', 'line'] as const;

/** セルの3状態。'default' = 行を作らない (既定に従う)。 */
type CellState = 'default' | 'on' | 'off';

type DraftCell = { state: CellState; channels: string[] | null };
type DraftMatrix = Record<string, DraftCell>; // key: `${event_type_id}:${recipient_role}`

const cellKey = (eventTypeId: string, role: RecipientRole): string => `${eventTypeId}:${role}`;

const buildDraftFromRows = (rows: DeliveryRow[]): DraftMatrix => {
  const draft: DraftMatrix = {};
  for (const r of rows) {
    const key = cellKey(r.event_type_id, r.recipient_role);
    if (r.source === 'default') {
      draft[key] = { state: 'default', channels: null };
    } else {
      draft[key] = { state: r.is_enabled ? 'on' : 'off', channels: r.channels };
    }
  }
  return draft;
};

export function NotificationSettingsMatrix({ basePath, canManage }: { basePath: string; canManage: boolean }) {
  const qc = useQueryClient();
  const queryKey = useMemo(() => ['notification-delivery-settings', basePath], [basePath]);
  const { data, isLoading } = useQuery<MatrixResponse>({
    queryKey, queryFn: () => apiFetch(basePath),
  });
  // ⚠️ QAレビュー Medium指摘: `GET /settings/notification-channels` は `notification:manage` を
  // 要求するが、この画面を開く管理者 (request_type_routing:manage / approval_workflow:manage) は
  // その権限を持つとは限らない。403 になると `tenantChannels` が空扱いになり、実際は有効な経路まで
  // 「未設定のため利用できません」と誤表示され選択不能になっていた。権限を問わない軽量な専用
  // エンドポイントを使う (返す情報はテナントの「どの経路が使えるか」という低感度の構成情報のみ)。
  const { data: enabledChannelCodes = [] } = useQuery<string[]>({
    queryKey: ['notifications-enabled-channels'],
    queryFn: () => apiFetch<{ channel_codes: string[] }>('/notifications/enabled-channels').then((r) => r.channel_codes),
  });
  const channelAvailability = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const code of enabledChannelCodes) m.set(code, true);
    return m;
  }, [enabledChannelCodes]);

  const [draft, setDraft] = useState<DraftMatrix | null>(null);
  useEffect(() => {
    if (data) setDraft(buildDraftFromRows(data.rows));
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!draft || !data) throw new Error('unreachable');
      const rows = data.event_types.flatMap((et) => (['approver', 'watcher'] as RecipientRole[])
        .map((role) => {
          const cell = draft[cellKey(et.event_type_id, role)] ?? { state: 'default' as CellState, channels: null };
          return { event_type_id: et.event_type_id, recipient_role: role, cell };
        })
        .filter((r) => r.cell.state !== 'default')
        .map((r) => ({
          event_type_id: r.event_type_id, recipient_role: r.recipient_role,
          is_enabled: r.cell.state === 'on', channels: r.cell.channels,
        })));
      return apiFetch<{ rows: DeliveryRow[] }>(basePath, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ rows }),
      });
    },
    onSuccess: (resp) => {
      setDraft(buildDraftFromRows(resp.rows));
      qc.setQueryData<MatrixResponse>(queryKey, (prev) => (prev ? { ...prev, rows: resp.rows } : prev));
      toast.success('通知設定を保存しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '通知設定の保存に失敗しました'),
  });

  if (isLoading || !data || !draft) return <div className="text-sm text-text-sub p-3">読み込み中…</div>;

  const setCell = (eventTypeId: string, role: RecipientRole, next: DraftCell) => {
    setDraft((prev) => ({ ...(prev ?? {}), [cellKey(eventTypeId, role)]: next }));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 text-xs text-text-sub bg-surface-sunken rounded p-2">
        <Info size={14} className="shrink-0 mt-0.5" aria-hidden />
        <p>
          「既定に従う」のままにすると、お知らせの種類ごとに決められた経路 (アプリ内・メール等) で送られます。
          「送らない」を選ぶとこの設定の対象ではその通知自体が作られません。
        </p>
      </div>
      {!data.event_types.some((et) => et.event_type_id === 'approval.auto_approved') && (
        <p className="text-xs text-text-sub">
          承認を経ない申請 (ゼロ段階承認) の受理通知は、申請書ごとの設定 (お知らせタブ) でのみ変更できます。
        </p>
      )}
      <div className="space-y-3">
        {data.event_types.map((et) => (
          <EventTypeCard
            key={et.event_type_id}
            eventType={et}
            rows={data.rows}
            draft={draft}
            canManage={canManage}
            channelAvailability={channelAvailability}
            onChangeCell={setCell}
          />
        ))}
      </div>
      {canManage && (
        <div className="flex justify-end">
          <Button variant="primary" size="sm" onClick={() => saveMutation.mutate()} loading={saveMutation.isPending}>
            通知設定を保存
          </Button>
        </div>
      )}
    </div>
  );
}

function EventTypeCard({
  eventType, rows, draft, canManage, channelAvailability, onChangeCell,
}: {
  eventType: EventTypeInfo;
  rows: DeliveryRow[];
  draft: DraftMatrix;
  canManage: boolean;
  channelAvailability: Map<string, boolean>;
  onChangeCell: (eventTypeId: string, role: RecipientRole, next: DraftCell) => void;
}) {
  const label = EVENT_TYPE_LABELS[eventType.event_type_id] ?? eventType.event_type_id;
  return (
    <div className="border border-surface-border rounded-md p-3 sm:p-4">
      <h4 className="text-sm font-semibold text-text-body mb-3">{label}</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {(['approver', 'watcher'] as RecipientRole[]).map((role) => {
          const key = cellKey(eventType.event_type_id, role);
          const cell = draft[key] ?? { state: 'default' as CellState, channels: null };
          const overriddenCount = rows.find((r) => r.event_type_id === eventType.event_type_id && r.recipient_role === role)
            ?.overridden_by_request_type_count;
          return (
            <RoleCell
              key={role}
              eventType={eventType}
              role={role}
              cell={cell}
              canManage={canManage}
              channelAvailability={channelAvailability}
              overriddenByRequestTypeCount={overriddenCount}
              onChange={(next) => onChangeCell(eventType.event_type_id, role, next)}
            />
          );
        })}
      </div>
    </div>
  );
}

function RoleCell({
  eventType, role, cell, canManage, channelAvailability, overriddenByRequestTypeCount, onChange,
}: {
  eventType: EventTypeInfo;
  role: RecipientRole;
  cell: DraftCell;
  canManage: boolean;
  channelAvailability: Map<string, boolean>;
  overriddenByRequestTypeCount?: number;
  onChange: (next: DraftCell) => void;
}) {
  const overridden = cell.state !== 'default';
  const defaultChannelsLabel = eventType.default_channels.map((c) => CHANNEL_LABELS[c] ?? c).join('・') || 'なし';
  // design-and-code レビュー Medium指摘: opt_out_allowed=0 の通知 (現状 approval.returned) は
  // 個人が購読解除できない「必須通知」として設計されている (増分⑤a/⑤b)。管理者トグルでも
  // 「送らない」を選べないようにし、サーバー側の拒否 (NOTIFICATION_OPT_OUT_NOT_ALLOWED) を
  // 待たずに理由を明示する。
  const cannotDisable = !eventType.opt_out_allowed;

  return (
    <div className={`rounded border p-2 sm:p-3 ${overridden ? 'border-misespark-navy bg-misespark-navy/5' : 'border-surface-border'}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-xs font-medium text-text-sub">{ROLE_LABELS[role]}</span>
        {overridden && (
          <span className="text-[11px] px-1.5 py-0.5 rounded bg-misespark-navy/10 text-misespark-navy">既定から変更</span>
        )}
      </div>
      <div className="flex flex-wrap gap-1" role="group" aria-label={`${ROLE_LABELS[role]}への${EVENT_TYPE_LABELS[eventType.event_type_id] ?? eventType.event_type_id}`}>
        {([
          { state: 'default' as CellState, label: '既定に従う' },
          { state: 'on' as CellState, label: '送る' },
          { state: 'off' as CellState, label: '送らない', disabledReason: cannotDisable },
        ]).map((seg) => (
          <button
            key={seg.state}
            type="button"
            disabled={!canManage || !!seg.disabledReason}
            aria-pressed={cell.state === seg.state}
            onClick={() => onChange(seg.state === 'on'
              ? { state: 'on', channels: cell.channels }
              : { state: seg.state, channels: null })}
            className={`min-h-[44px] px-3 py-1.5 rounded text-xs border ${
              cell.state === seg.state
                ? 'bg-misespark-navy text-white border-misespark-navy'
                : 'bg-white text-text-body border-surface-border'
            } ${canManage ? '' : 'opacity-60'}`}
          >
            {seg.label}
          </button>
        ))}
      </div>
      {cannotDisable && (
        <p className="text-[11px] text-text-sub mt-1">この通知は必須のため送信を停止できません。</p>
      )}
      {cell.state === 'default' && (
        <p className="text-[11px] text-text-sub mt-1">既定の経路: {defaultChannelsLabel}</p>
      )}
      {cell.state === 'on' && (
        <div className="mt-2 flex flex-wrap gap-2">
          {ALL_CHANNELS.map((code) => {
            const currentChannels = cell.channels ?? eventType.default_channels;
            const checked = currentChannels.includes(code);
            const available = channelAvailability.get(code) ?? false;
            const inputId = `ntf-channel-${eventType.event_type_id}-${role}-${code}`;
            // design-expert レビュー Minor指摘: 全チェックを外すと channels=[] が保存され
            // サーバー側 (notificationDeliveryChannelsSchema.min(1)) の400で初めて気づく形だった。
            // 最後の1件は外せないようにする (少なくとも1経路を維持)。
            const isOnlyChecked = checked && currentChannels.length === 1;
            return (
              <label key={code} htmlFor={inputId} className={`inline-flex items-center gap-1 text-xs px-2 py-1.5 min-h-[44px] rounded border ${available ? 'border-surface-border' : 'border-surface-border opacity-50'}`}>
                <input
                  id={inputId}
                  type="checkbox"
                  checked={checked}
                  disabled={!canManage || !available || isOnlyChecked}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...new Set([...currentChannels, code])]
                      : currentChannels.filter((c) => c !== code);
                    onChange({ state: 'on', channels: next });
                  }}
                  className="min-w-[16px] min-h-[16px]"
                />
                {CHANNEL_LABELS[code] ?? code}
                {!available && <span className="sr-only">(このテナントでは未設定のため利用できません)</span>}
                {isOnlyChecked && <span className="sr-only">(少なくとも1つの経路が必要です)</span>}
              </label>
            );
          })}
        </div>
      )}
      {cell.state === 'on' && ALL_CHANNELS.some((c) => !(channelAvailability.get(c) ?? false)) && (
        <p className="text-[11px] text-text-sub mt-1">
          グレー表示の経路はこのテナントで未設定のため送信されません。
          {canManage && (
            <>
              {' '}
              <a href="/settings/notifications" className="text-brand-red underline">通知設定で有効化する</a>
            </>
          )}
        </p>
      )}
      {/* §8-5「申請書側で上書きされている件数を出す」: ②層のみ overridden_by_request_type_count を返す
          (①層は常にサーバーが undefined を返す)。①層は常に②層より優先するため、ここで OFF にしても
          申請書側の設定があれば実際には送られ続けることを警告する。 */}
      {!!overriddenByRequestTypeCount && overriddenByRequestTypeCount > 0 && (
        <p className="text-[11px] text-alert-text mt-1">
          申請書ごとの設定で上書きしている申請種別が{overriddenByRequestTypeCount}件あります (そちらが優先されます)。
        </p>
      )}
    </div>
  );
}
